import type { InboxChannel, InboxKind } from '../../types/inbox.js';
import type {
  CommunityReportingThread,
  SocialCommunity,
  SocialDateRange,
} from '../../types/socialReporting.js';
import { periodDelta } from './socialOverview.js';

// Community & Reputation reporting — pure aggregation. THE DIFFERENTIATOR tab's
// math: engagement handled, responsiveness (response rate + median response time),
// and reviews (period-scoped average + distribution + reply rate + rating trend).
//
// No I/O, no Date.now (dates are injected). Everything is unit-tested off the
// SocialCommunity projection (the community module's per-thread inbound/first-reply
// timestamps). The threads arrive over the UNION window [previousRange.from ..
// range.to]; this lib splits current vs previous by inbound date.
//
// ═══ THE GUARD THIS MODULE ENFORCES (mutation-tested) ═══
// We hold only a RECENT ingested slice of reviews (a 7-day recency cutoff at
// ingest) and the GBP batch endpoint deliberately omits averageRating /
// totalReviewCount — so we CANNOT compute a business's true overall/lifetime star
// rating from ingested data. `periodAverageRating` averages ONLY reviews RECEIVED
// IN THE PERIOD; presenting a period-average dressed up as "your Google rating"
// would be a materially false claim to a client. Removing the in-period filter
// must fail a test.

// ---------------------------------------------------------------------------
// windowing
// ---------------------------------------------------------------------------

/** Inclusive date-prefix membership (inboundAt is ISO; compare its YYYY-MM-DD). */
function inRange(iso: string, range: SocialDateRange): boolean {
  const day = iso.slice(0, 10);
  return day >= range.from && day <= range.to;
}

function threadsInRange(
  threads: CommunityReportingThread[],
  range: SocialDateRange,
): CommunityReportingThread[] {
  return threads.filter((t) => inRange(t.inboundAt, range));
}

/**
 * Total inbound items (comments + DMs + reviews) whose "received" time falls in
 * the window — the "Community handled" figure. Shared by the Community tab's §1
 * total and the Overview KPI so the two surfaces agree by construction.
 */
export function communityHandledCount(
  threads: CommunityReportingThread[],
  range: SocialDateRange,
): number {
  return threadsInRange(threads, range).length;
}

// ---------------------------------------------------------------------------
// small numeric helpers
// ---------------------------------------------------------------------------

/** Median of a numeric list (avg of the two middles for even counts); null if empty. */
export function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}

/** Mean of a numeric list; null if empty. */
function mean(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Response time in MINUTES for one thread: first outgoing reply minus the inbound
 * time. null when unanswered, when either timestamp is unparseable, or when the
 * reply predates the inbound (a data oddity — excluded from the median, still
 * counts as "answered" for the rate). Never negative.
 */
export function responseTimeMinutes(thread: CommunityReportingThread): number | null {
  if (!thread.firstReplyAt) return null;
  const inbound = Date.parse(thread.inboundAt);
  const reply = Date.parse(thread.firstReplyAt);
  if (Number.isNaN(inbound) || Number.isNaN(reply)) return null;
  if (reply < inbound) return null;
  return (reply - inbound) / 60_000;
}

/** A thread is "answered" if it got a reply OR was resolved (either is service delivered). */
function isAnswered(thread: CommunityReportingThread): boolean {
  return thread.firstReplyAt !== null || thread.status === 'resolved';
}

// ---------------------------------------------------------------------------
// §1 — Engagement handled
// ---------------------------------------------------------------------------

export interface CommunityDelta {
  current: number;
  previous: number;
  deltaPct: number | null;
  direction: 'up' | 'down' | 'flat' | null;
}

function delta(current: number, previous: number): CommunityDelta {
  const { deltaPct, direction } = periodDelta(current, previous);
  return { current, previous, deltaPct, direction };
}

export interface EngagementBreakdownRow<K extends string> {
  key: K;
  current: number;
  previous: number;
}

export interface EngagementHandledView {
  total: CommunityDelta;
  byKind: EngagementBreakdownRow<InboxKind>[];
  byChannel: EngagementBreakdownRow<InboxChannel>[];
  /** Weekly stacked volume (current period) — one row per ISO-Monday week. */
  weekly: WeeklyVolumeRow[];
  isEmpty: boolean;
}

export interface WeeklyVolumeRow {
  weekStart: string; // Monday YYYY-MM-DD (UTC)
  comment: number;
  dm: number;
  review: number;
  total: number;
}

// Fixed display order so a breakdown is stable regardless of what's present.
const KIND_ORDER: InboxKind[] = ['comment', 'dm', 'review'];
const CHANNEL_ORDER: InboxChannel[] = ['facebook', 'instagram', 'googlebusiness', 'metaads'];

function countBy<K extends string>(
  threads: CommunityReportingThread[],
  key: (t: CommunityReportingThread) => K,
): Map<K, number> {
  const m = new Map<K, number>();
  for (const t of threads) m.set(key(t), (m.get(key(t)) ?? 0) + 1);
  return m;
}

/** The Monday (UTC) of the ISO week containing the given ISO instant. */
export function weekStart(iso: string): string {
  const day = iso.slice(0, 10);
  const t = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(t)) return day;
  const d = new Date(t);
  const dow = d.getUTCDay(); // 0=Sun..6=Sat
  const backToMonday = (dow + 6) % 7; // days since Monday
  return new Date(t - backToMonday * 86_400_000).toISOString().slice(0, 10);
}

function buildWeeklyVolume(current: CommunityReportingThread[]): WeeklyVolumeRow[] {
  const byWeek = new Map<string, WeeklyVolumeRow>();
  for (const t of current) {
    const wk = weekStart(t.inboundAt);
    const row =
      byWeek.get(wk) ?? { weekStart: wk, comment: 0, dm: 0, review: 0, total: 0 };
    row[t.kind] += 1;
    row.total += 1;
    byWeek.set(wk, row);
  }
  return [...byWeek.values()].sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}

export function buildEngagementHandled(data: SocialCommunity): EngagementHandledView {
  const current = threadsInRange(data.threads, data.range);
  const previous = threadsInRange(data.threads, data.previousRange);

  const curByKind = countBy(current, (t) => t.kind);
  const prevByKind = countBy(previous, (t) => t.kind);
  const byKind: EngagementBreakdownRow<InboxKind>[] = KIND_ORDER.filter(
    (k) => (curByKind.get(k) ?? 0) > 0 || (prevByKind.get(k) ?? 0) > 0,
  ).map((k) => ({ key: k, current: curByKind.get(k) ?? 0, previous: prevByKind.get(k) ?? 0 }));

  const curByChannel = countBy(current, (t) => t.channel);
  const prevByChannel = countBy(previous, (t) => t.channel);
  const byChannel: EngagementBreakdownRow<InboxChannel>[] = CHANNEL_ORDER.filter(
    (c) => (curByChannel.get(c) ?? 0) > 0 || (prevByChannel.get(c) ?? 0) > 0,
  ).map((c) => ({ key: c, current: curByChannel.get(c) ?? 0, previous: prevByChannel.get(c) ?? 0 }));

  return {
    total: delta(current.length, previous.length),
    byKind,
    byChannel,
    weekly: buildWeeklyVolume(current),
    isEmpty: current.length === 0 && previous.length === 0,
  };
}

// ---------------------------------------------------------------------------
// §2 — Responsiveness
// ---------------------------------------------------------------------------

export interface ResponsivenessSnapshot {
  total: number;
  answered: number;
  /** 0-100, or null when there were no inbound items to respond to. */
  responseRate: number | null;
  /** Median minutes from inbound → first reply (answered threads only); null if none. */
  medianResponseMinutes: number | null;
  /** Threads still unresolved (status ≠ resolved). */
  outstanding: number;
}

export interface ResponsivenessView {
  current: ResponsivenessSnapshot;
  previous: ResponsivenessSnapshot;
  responseRateDelta: CommunityDelta | null;
  medianDelta: CommunityDelta | null;
  isEmpty: boolean;
}

function snapshot(threads: CommunityReportingThread[]): ResponsivenessSnapshot {
  const total = threads.length;
  const answered = threads.filter(isAnswered).length;
  const times = threads
    .map(responseTimeMinutes)
    .filter((v): v is number => v !== null);
  return {
    total,
    answered,
    responseRate: total > 0 ? (answered / total) * 100 : null,
    medianResponseMinutes: median(times),
    outstanding: threads.filter((t) => t.status !== 'resolved').length,
  };
}

/**
 * Responsiveness covers ALL inbound kinds (comments + DMs + reviews) — every
 * thread has an inbound time and possibly a first reply, and for a GBP-heavy
 * automotive client reviews ARE the responsiveness story. Threads with no reply
 * count toward the response rate (as unanswered, unless resolved) but are excluded
 * from the response-time median (no valid interval to measure).
 */
export function buildResponsiveness(data: SocialCommunity): ResponsivenessView {
  const current = snapshot(threadsInRange(data.threads, data.range));
  const previous = snapshot(threadsInRange(data.threads, data.previousRange));
  return {
    current,
    previous,
    responseRateDelta:
      current.responseRate !== null && previous.responseRate !== null
        ? delta(current.responseRate, previous.responseRate)
        : null,
    medianDelta:
      current.medianResponseMinutes !== null && previous.medianResponseMinutes !== null
        ? delta(current.medianResponseMinutes, previous.medianResponseMinutes)
        : null,
    isEmpty: current.total === 0 && previous.total === 0,
  };
}

// ---------------------------------------------------------------------------
// §3 — Reviews & reputation
// ---------------------------------------------------------------------------

// The label that keeps the average honest. The rating is the mean of reviews
// RECEIVED THIS PERIOD — never a claimed overall/lifetime rating (we don't hold
// the full history, and the batch endpoint omits the true aggregate).
export const PERIOD_RATING_NOTE =
  'Average of reviews received this period — not an overall Google or Facebook rating.';

// Reviews, optionally scoped to a channel set. The channel filter is how the
// reputation snapshot is split between surfaces: the Google Business tab reads
// ['googlebusiness'], the Community tab's Facebook-review item reads ['facebook'].
// `null` = every channel (the original all-channel behaviour).
function reviewsOnly(
  threads: CommunityReportingThread[],
  channels: InboxChannel[] | null = null,
): CommunityReportingThread[] {
  return threads.filter(
    (t) => t.kind === 'review' && (channels === null || channels.includes(t.channel)),
  );
}

/**
 * ⚠️ THE PERIOD-SCOPED GATE (mutation-tested). Averages the ratings of reviews
 * RECEIVED IN `range` ONLY. A review outside the period MUST NOT affect this —
 * removing the `inRange` filter turns a period-average into a false "overall
 * rating" claim, and a test asserts an out-of-period review is ignored. `channels`
 * optionally scopes to a review surface (Google vs Facebook).
 */
export function periodAverageRating(
  threads: CommunityReportingThread[],
  range: SocialDateRange,
  channels: InboxChannel[] | null = null,
): number | null {
  const ratings = reviewsOnly(threads, channels)
    .filter((t) => inRange(t.inboundAt, range))
    .map((t) => t.rating)
    .filter((r): r is number => typeof r === 'number');
  return mean(ratings);
}

export interface StarDistributionRow {
  stars: 1 | 2 | 3 | 4 | 5;
  count: number;
}

export interface RatingTrendPoint {
  weekStart: string;
  count: number;
  averageRating: number | null;
}

export interface RecentReviewRow {
  id: string;
  rating: number | null;
  text: string;
  replied: boolean;
  channel: InboxChannel;
  authorName: string;
  at: string; // inbound ISO
  permalink: string | null;
}

export interface ReviewsReputationView {
  reviewsReceived: CommunityDelta;
  /** Period-scoped average (never overall). null when no rated reviews this period. */
  averageRating: number | null;
  averageRatingNote: string;
  /** Star counts 5→1 (display order). */
  starDistribution: StarDistributionRow[];
  /** % of this period's reviews carrying an owner reply; null when no reviews. */
  replyRate: number | null;
  /** Average rating + volume per ISO-Monday week, over the current period. */
  ratingTrend: RatingTrendPoint[];
  recentReviews: RecentReviewRow[];
  isEmpty: boolean;
}

const RECENT_REVIEWS_LIMIT = 8;

function buildRatingTrend(current: CommunityReportingThread[]): RatingTrendPoint[] {
  const byWeek = new Map<string, CommunityReportingThread[]>();
  for (const t of current) {
    const wk = weekStart(t.inboundAt);
    const list = byWeek.get(wk);
    if (list) list.push(t);
    else byWeek.set(wk, [t]);
  }
  return [...byWeek.entries()]
    .map(([wk, list]) => ({
      weekStart: wk,
      count: list.length,
      averageRating: mean(
        list.map((t) => t.rating).filter((r): r is number => typeof r === 'number'),
      ),
    }))
    .sort((a, b) => a.weekStart.localeCompare(b.weekStart));
}

/** All-channel reputation snapshot (Facebook + Google). */
export function buildReviewsReputation(data: SocialCommunity): ReviewsReputationView {
  return buildReviewsReputationForChannels(data, null);
}

/**
 * Channel-scoped reputation snapshot. `channels` = null → every channel;
 * ['googlebusiness'] → the Google Business tab's Google-review snapshot;
 * ['facebook'] → the Community tab's Facebook-review item. The period-scoped
 * average-rating gate applies within the channel scope (never a lifetime rating).
 */
export function buildReviewsReputationForChannels(
  data: SocialCommunity,
  channels: InboxChannel[] | null,
): ReviewsReputationView {
  const current = reviewsOnly(threadsInRange(data.threads, data.range), channels);
  const previous = reviewsOnly(threadsInRange(data.threads, data.previousRange), channels);

  const distCounts = new Map<number, number>();
  for (const r of current) {
    if (typeof r.rating === 'number') {
      const s = Math.min(5, Math.max(1, Math.round(r.rating)));
      distCounts.set(s, (distCounts.get(s) ?? 0) + 1);
    }
  }
  const starDistribution: StarDistributionRow[] = ([5, 4, 3, 2, 1] as const).map((stars) => ({
    stars,
    count: distCounts.get(stars) ?? 0,
  }));

  const recentReviews: RecentReviewRow[] = [...current]
    .sort((a, b) => b.inboundAt.localeCompare(a.inboundAt))
    .slice(0, RECENT_REVIEWS_LIMIT)
    .map((t) => ({
      id: t.id,
      rating: t.rating,
      text: t.reviewText ?? '',
      replied: t.reviewReplied,
      channel: t.channel,
      authorName: t.authorName ?? 'Anonymous',
      at: t.inboundAt,
      permalink: t.permalink,
    }));

  return {
    reviewsReceived: delta(current.length, previous.length),
    averageRating: periodAverageRating(data.threads, data.range, channels),
    averageRatingNote: PERIOD_RATING_NOTE,
    starDistribution,
    replyRate:
      current.length > 0
        ? (current.filter((r) => r.reviewReplied).length / current.length) * 100
        : null,
    ratingTrend: buildRatingTrend(current),
    recentReviews,
    isEmpty: current.length === 0 && previous.length === 0,
  };
}

// ---------------------------------------------------------------------------
// §2b — Outstanding / unresponded worklist
// ---------------------------------------------------------------------------

// ⚠️ THE COUNT IS ALL-KINDS, THE DETAIL LIST IS DM+REVIEW-ONLY (unit-tested).
// The headline outstanding count aggregates EVERY unresolved kind (comments + DMs
// + reviews) so it isn't understated; but the detail list shows only the
// unresolved DMs and Reviews — comments are counted, not listed (they'd drown the
// signal). This is a READ-ONLY report surface (no reply actions).

export interface OutstandingItem {
  id: string;
  kind: InboxKind;
  channel: InboxChannel;
  authorName: string;
  /** Received time (first inbound), ISO. */
  at: string;
  /** DM / comment / review body text (null when nothing was captured). */
  text: string | null;
  /** Review rating (null for DMs). */
  rating: number | null;
  permalink: string | null;
}

export interface OutstandingView {
  /** ALL unresolved kinds in the period (comments + DMs + reviews). Never narrowed. */
  total: number;
  byKind: EngagementBreakdownRow<InboxKind>[];
  /** Unresolved DMs, newest first (the detail list). */
  dms: OutstandingItem[];
  /** Unresolved reviews, newest first (the detail list). */
  reviews: OutstandingItem[];
  isEmpty: boolean;
}

function isUnresolved(t: CommunityReportingThread): boolean {
  return t.status !== 'resolved';
}

function toOutstandingItem(t: CommunityReportingThread): OutstandingItem {
  return {
    id: t.id,
    kind: t.kind,
    channel: t.channel,
    authorName: t.authorName ?? 'Anonymous',
    at: t.inboundAt,
    text: t.kind === 'review' ? (t.reviewText ?? t.inboundText) : t.inboundText,
    rating: t.rating,
    permalink: t.permalink,
  };
}

export function buildOutstandingView(data: SocialCommunity): OutstandingView {
  const unresolved = threadsInRange(data.threads, data.range).filter(isUnresolved);

  const counts = countBy(unresolved, (t) => t.kind);
  const byKind: EngagementBreakdownRow<InboxKind>[] = KIND_ORDER.filter(
    (k) => (counts.get(k) ?? 0) > 0,
  ).map((k) => ({ key: k, current: counts.get(k) ?? 0, previous: 0 }));

  const byNewest = (a: OutstandingItem, b: OutstandingItem) => b.at.localeCompare(a.at);
  const dms = unresolved.filter((t) => t.kind === 'dm').map(toOutstandingItem).sort(byNewest);
  const reviews = unresolved
    .filter((t) => t.kind === 'review')
    .map(toOutstandingItem)
    .sort(byNewest);

  return {
    total: unresolved.length, // ALL kinds — comments included in the count
    byKind,
    dms,
    reviews,
    isEmpty: unresolved.length === 0,
  };
}

// ---------------------------------------------------------------------------
// top-level view
// ---------------------------------------------------------------------------

export interface CommunityReportingView {
  engagement: EngagementHandledView;
  responsiveness: ResponsivenessView;
  outstanding: OutstandingView;
  /**
   * FACEBOOK-only review reputation (the reviews-split rule): Google review
   * reputation lives on the Google Business tab, so the Community tab carries only
   * the Facebook-review item — rendered by the tab only when it's non-empty. All
   * review channels still feed `responsiveness` (how fast we replied); only the
   * reputation SNAPSHOT splits by surface.
   */
  facebookReviews: ReviewsReputationView;
  generatedAt: string;
}

export function buildCommunityReportingView(data: SocialCommunity): CommunityReportingView {
  return {
    engagement: buildEngagementHandled(data),
    responsiveness: buildResponsiveness(data),
    outstanding: buildOutstandingView(data),
    facebookReviews: buildReviewsReputationForChannels(data, ['facebook']),
    generatedAt: data.generatedAt,
  };
}

// ---------------------------------------------------------------------------
// display formatting
// ---------------------------------------------------------------------------

/** Humanise a duration in minutes ("2h 5m", "3d", "< 1m"); null → em dash. */
export function formatResponseTime(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes < 1) return '< 1m';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  if (minutes < 60 * 24) {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(minutes / (60 * 24));
  const h = Math.round((minutes % (60 * 24)) / 60);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

/** Format a percentage (0-100) to one decimal; null → em dash. */
export function formatRate(rate: number | null): string {
  return rate === null ? '—' : `${rate.toFixed(1)}%`;
}

/** Format a delta as a signed percentage (e.g. "+12.4%"); null → no comparison. */
export function formatCommunityDelta(d: CommunityDelta | null): string | null {
  if (!d || d.deltaPct === null) return null;
  const sign = d.deltaPct > 0 ? '+' : '';
  return `${sign}${d.deltaPct.toFixed(1)}%`;
}
